/**
 * Shared error triage — what counts as a REAL error, and how severe it is.
 *
 * Why this module exists (2026-07-25): the noise filters used to live only in
 * `components/ClientErrorReporter.tsx`, so they ran ONLY in the browser, ONLY
 * when the visitor had the current bundle. `POST /api/client-errors` is public
 * and unauthenticated, so a stale tab across a deploy — or anyone with `curl` —
 * wrote straight past every filter into `app_errors`. Worse, the table is
 * pruned at a soft cap, so junk rows EVICT real ones.
 *
 * Both the reporter and the ingest route now import from here, so a filter can
 * never apply on one side and not the other.
 *
 * Three questions, in order:
 *   1. `isNoiseError`  — is this not our code at all? (drop it, never stored)
 *   2. `isBenignNetworkError` — is this an aborted/offline request? (drop it)
 *   3. `classifyErrorLevel` — real, but did it self-heal? (downgrade severity)
 *
 * Deliberately dependency-free so it is safe to import from a client component
 * and from a Worker route handler alike.
 */

import type { ErrorLevel } from "./app-errors";

/**
 * Third-party-injection noise we can't fix and shouldn't log — it pollutes the
 * error table without ever pointing at a real bug in our code:
 *  - `window.webkit.messageHandlers` — iOS WKWebView native bridge, injected by
 *    in-app browsers (Instagram/Facebook/etc.) and some iOS extensions.
 *  - `_AutofillCallbackHandler` — password-manager / browser autofill shim.
 *  - ResizeObserver loop notices, "Non-Error promise rejection", and various
 *    known in-app-browser / extension bridge globals.
 * These arrive via window.onerror with our own page as the filename (they run
 * inline in the page), so only a message match catches them.
 */
export const NOISE_ERROR_RE =
  /webkit\.messageHandlers|_AutofillCallbackHandler|ResizeObserver loop (limit exceeded|completed)|Non-Error promise rejection|instantSearchSDKJSBridge|gCrWeb|__gCrWeb|__firefox__|atomicFindClose|MyApp_RemoveAllHighlights|vid_mate_check|__edgeTrackingPreventionStatistics|Java(Script)? bridge/i;

/** Extension / native-bridge origins — never our code. */
export const NOISE_URL_RE =
  /^(chrome|chrome-untrusted|moz|safari|safari-web|webkit-masked|ms-browser|edge|extension)-?extension:\/\//i;

/**
 * Extension frames inside a stack. A rejection or a React boundary report
 * carries no `url` (the reporter substitutes `window.location.href`), so the
 * URL filter above is blind on those paths — the stack is the only place the
 * extension origin still shows up.
 */
export const NOISE_STACK_RE =
  /(chrome|moz|safari|safari-web|webkit-masked|ms-browser|edge)-extension:\/\//i;

/**
 * Aborted / offline / cancelled network requests. NOT bugs:
 *
 * "Failed to fetch" (Chrome), "Load failed" (Safari), "NetworkError when
 * attempting to fetch resource" (Firefox) and the various AbortError phrasings
 * are the SAME generic `TypeError` the platform raises when a request is
 * cancelled by a navigation, a closing tab, a flaky mobile connection, or an
 * ad-blocker. The browser deliberately gives no detail, so the row that lands
 * in the log has no failing URL, no stack worth reading, and nothing to fix.
 *
 * Every `fetch` we own is already inside a try/catch (`adminFetch`, the admin
 * auto-refresh loaders, the push helpers), so an UNHANDLED one is by definition
 * a stray — most often Next's own RSC route prefetch for a `<Link>` the user
 * navigated away from. This is the "Failed to fetch on /admin/customers"
 * class of false positive that prompted the 2026-07-25 review.
 *
 * A network failure that genuinely matters surfaces as a server-side error or
 * as a visible UI failure state — not as an anonymous rejection.
 *
 * FULLY ANCHORED — every alternative must match the WHOLE message. An earlier
 * version left `AbortError` and friends as bare substring alternatives, which
 * silently swallowed real failures that merely mention them, most importantly
 * `AbortError: Registration failed - push service error` from
 * `pushManager.subscribe()` — a load-bearing admin-push failure. If you add an
 * alternative here, anchor it.
 */
export const BENIGN_NETWORK_RE =
  /^(?:TypeError:\s*|AbortError:\s*|DOMException:\s*)?(?:Failed to fetch|NetworkError when attempting to fetch resource|Load failed|Network request failed|The Internet connection appears to be offline|cancelled|canceled|aborted|Aborted|Fetch is aborted|The (?:user aborted a request|operation was aborted)|signal is aborted without reason|The request timed out|AbortError)\.?$/i;

/**
 * A chunk-load failure means the client is running a STALE app version whose
 * webpack/Turbopack runtime references chunk hashes a newer deploy has rotated
 * away (common after a deploy while a tab or the installed PWA was open). One
 * reload pulls the fresh HTML + chunk manifest and fixes it.
 *
 * These alternatives are DEFINITIVE — the message alone proves a chunk problem.
 */
export const CHUNK_ERROR_RE =
  /Loading chunk [\w-]+ failed|ChunkLoadError|Loading CSS chunk|\.undefined\.js|error loading dynamically imported module|Failed to fetch dynamically imported module|Importing a module script failed/i;

/**
 * The minified module-factory call `__webpack_modules__[moduleId](…)`. When a
 * chunk loads 200-OK but its id is absent from the current runtime manifest,
 * that factory is `undefined` and you get exactly `b[e] is not a function`,
 * thrown synchronously inside the render of a lazy child — which a React error
 * boundary then reports as a render bug.
 *
 * AMBIGUOUS ON ITS OWN — this must NEVER be treated as a chunk error from the
 * message alone. In a minified production bundle EVERY identifier is 1-3
 * characters, so a genuine bug of ours (`handlers[key](…)`, `fns[i](…)`) also
 * reports as `n[e] is not a function`. Matching on shape alone would give a
 * real bug a surprise page reload AND downgrade it to `warning`, hiding it —
 * exactly the failure mode this whole review exists to remove.
 *
 * So it only counts as a chunk error with CORROBORATION: an actual
 * `/_next/static/` resource must have failed to load in this session
 * (see `markChunkLoadFailure`). That signal is definitive — a missing chunk
 * file IS the stale deploy.
 */
export const MINIFIED_MODULE_FACTORY_RE = /^\w{1,3}\[\w{1,3}\] is not a function\.?$/;

/**
 * Set when a `/_next/static/chunks/` script or stylesheet fails to load, which
 * happens when the running bundle references files a newer deploy rotated away.
 *
 * EXPIRES after 60s. A dropped route-prefetch on a flaky mobile connection also
 * trips this, and a permanent latch would then let any later genuine
 * `handlers[key] is not a function` be misread as a stale chunk — earning it a
 * surprise page reload and a downgrade to `warning`, i.e. exactly the defect
 * the corroboration exists to prevent. A real stale-deploy failure and the
 * module-factory error it causes happen within milliseconds of each other, so a
 * short window loses nothing.
 *
 * Browser-session state. The server imports this module too, but nothing there
 * ever calls `markChunkLoadFailure`, so it stays unset and the server simply
 * never applies the corroborated branch — the conservative direction.
 */
const CHUNK_FAILURE_TTL_MS = 60_000;
let chunkLoadFailureAt = 0;

/** Now, via an injectable clock so tests don't have to wait 60s. */
let nowMs: () => number = () => Date.now();

export function markChunkLoadFailure(): void {
  chunkLoadFailureAt = nowMs();
}

export function hasSeenChunkLoadFailure(): boolean {
  return chunkLoadFailureAt > 0 && nowMs() - chunkLoadFailureAt < CHUNK_FAILURE_TTL_MS;
}

/** Test-only: reset the session flag (and any clock override) between cases. */
export function resetChunkLoadFailureForTests(): void {
  chunkLoadFailureAt = 0;
  nowMs = () => Date.now();
}

/** Test-only: control the clock used for the corroboration window. */
export function setChunkClockForTests(fn: () => number): void {
  nowMs = fn;
}

/**
 * True when this error is a stale-deploy chunk problem — safe to self-heal
 * with a one-shot reload and to record as a `warning` rather than an `error`.
 */
export function isStaleChunkError(message: string, stack?: string | null): boolean {
  const haystack = `${message ?? ""}\n${stack ?? ""}`;
  if (CHUNK_ERROR_RE.test(haystack)) return true;
  // Corroborated only — see MINIFIED_MODULE_FACTORY_RE.
  return hasSeenChunkLoadFailure() && MINIFIED_MODULE_FACTORY_RE.test((message ?? "").trim());
}

/**
 * React errors the runtime RECOVERS from on its own. #418/#419/#423/#425 are
 * the hydration-mismatch family: React discards the server HTML for that
 * subtree and re-renders it on the client, so the visitor sees correct output.
 * Worth knowing about (it costs a client re-render and can flash), but logging
 * it at `error` next to a real crash is what made the log untrustworthy.
 */
export const RECOVERABLE_REACT_RE = /Minified React error #(418|419|423|425)\b/;

export interface TriageInput {
  message: string;
  url?: string | null;
  stack?: string | null;
  /**
   * Where the report came from. Only `react-boundary` changes behaviour, and
   * it does so in the SAFE direction — see `shouldDropError`.
   */
  source?: string | null;
}

/** Not our code — never store it. */
export function isNoiseError({ message, url, stack }: TriageInput): boolean {
  if (message && NOISE_ERROR_RE.test(message)) return true;
  if (url && NOISE_URL_RE.test(url)) return true;
  if (stack && NOISE_STACK_RE.test(stack)) return true;
  return false;
}

/** An aborted/offline request — real network behaviour, not a bug. */
export function isBenignNetworkError(message: string): boolean {
  return Boolean(message) && BENIGN_NETWORK_RE.test(message.trim());
}

/**
 * True when the report should never reach the database at all.
 *
 * A `react-boundary` report is NEVER dropped for being network-shaped. Reaching
 * a boundary means the UI actually broke and the visitor was shown an error
 * screen — that is a real, user-visible failure whatever the message says. A
 * server-component/RSC fetch failure surfaces with the literal text
 * "Failed to fetch", so treating boundary reports like stray rejections would
 * discard the most serious errors the app can produce. Injection noise is still
 * filtered on every path (an extension frame is never our code).
 */
export function shouldDropError(input: TriageInput): boolean {
  if (isNoiseError(input)) return true;
  if (input.source === "react-boundary") return false;
  return isBenignNetworkError(input.message);
}

/**
 * Severity for a report we ARE keeping. Self-healing conditions are recorded
 * as warnings so they stay visible for trend-spotting without counting toward
 * the sidebar's "errors in the last 24h" badge (which filters `level='error'`).
 */
export function classifyErrorLevel(
  input: TriageInput,
  requested: ErrorLevel = "error"
): ErrorLevel {
  // An explicit non-error level from the caller is always respected.
  if (requested !== "error") return requested;
  if (RECOVERABLE_REACT_RE.test(input.message)) return "warning";
  if (isStaleChunkError(input.message, input.stack)) return "warning";
  return "error";
}
